import React, { useState } from 'react';
import { Grid, styled, FormControl, FormControlLabel, FormLabel, Radio, RadioGroup, Button } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ContentCourseSelector from './ContentCourseSelector';
import ContentCourseCard from '../Cards/ContentCourseCard';
import { CourseDeliveryDTO, CourseSetDTO } from '@beable/roster-backend-client-v1';
import CourseSetSelector from './CourseSetSelector';
import { getStyles } from '../../../theme/styles';
import CourseSetDialog from '../../Courses/CourseSets/Dialog/CourseSetDialog';
import CourseSetCard from '../Cards/CourseSetCard';

const ProvisionTypeLabel = styled(FormLabel)(({ theme }) => ({
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(1),
}));

enum ProvisionByType {
    Course = 'Course',
    CourseSet = 'CourseSet',
}

interface ProvisionTypeSelectorProps {
  course?: CourseDeliveryDTO;
  courseSet?: CourseSetDTO;
  locationCode?: string;
  customerId?: string;
  siteId?: number;
  onCourseChange: any;
  onCourseSetChange: any;
  disabled?: boolean;
  className?: string;
  inPanel?: boolean;
}

export default function ProvisionTypeSelector({
  course, courseSet, locationCode, customerId, siteId, onCourseChange, onCourseSetChange, disabled, className, inPanel
}: ProvisionTypeSelectorProps) {
  const theme = useTheme();
  const styles = getStyles(theme);
  const [provisionType, setProvisionType] = useState<ProvisionByType>('' as ProvisionByType);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [refreshCourseSets, setRefreshCourseSets] = useState(false);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setProvisionType(event.target.value as  ProvisionByType);
    onCourseChange(undefined);
    onCourseSetChange(undefined);
  };

  const handleDialogClose = async () => {
    setIsModalOpen(false);
    setRefreshCourseSets(true);
  }

  return (
    <>
      <FormControl variant="standard" sx={inPanel ? styles.pFormControlPanel : styles.pFormControl}>
        <ProvisionTypeLabel>Provision By:</ProvisionTypeLabel>
        <RadioGroup row
          aria-label="provision by"
          value={provisionType}
          onChange={handleChange}
        >
          <FormControlLabel
            value={ProvisionByType.Course}
            control={<Radio />}
            label='Course'
            disabled={disabled}
          />
          <FormControlLabel
            value={ProvisionByType.CourseSet}
            control={<Radio />}
            label='Course Set'
            disabled={disabled}
          />
        </RadioGroup>
      </FormControl>
      { provisionType === ProvisionByType.Course &&
        <ContentCourseSelector
          courseId={course ? course.courseId : ''}
          locationCode={locationCode ?? ''}
          onChange={onCourseChange}
          variant='outlined'
          disabled={disabled}
          className={className}
          inPanel={inPanel}
        />
      }
      { provisionType === ProvisionByType.CourseSet &&
        <>
          <CourseSetSelector
            courseSetId={courseSet?.id}
            locationCode={locationCode}
            siteId={siteId}
            onChange={onCourseSetChange}
            variant='outlined'
            disabled={!siteId || disabled}
            refresh={refreshCourseSets}
            className={className}
            inPanel={inPanel}
          />
          <Grid container sx={{
            justifyContent: 'flex-end'
          }}>
            <Button
              variant='contained'
              color='primary'
              aria-label='add new course set'
              title='Add New Course Set'
              onClick={() => {setRefreshCourseSets(false); setIsModalOpen(true)}}
              disabled={disabled}
            >
              Add New
            </Button>
          </Grid>
          <CourseSetDialog
            open={isModalOpen}
            handleClose={handleDialogClose}
            locationCode={locationCode}
            customerId={customerId}
            siteId={siteId}
          />
        </>
      }
      { course &&
        <ContentCourseCard
          className={className}
          inPanel={inPanel}
        />
      }
      { courseSet &&
        <CourseSetCard
          className={className}
          inPanel={inPanel}
        />
      }
    </>
  );
}
